// src/components/paginas/modals/UserPanelModal.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../../auth/AuthContext';

const API_BASE = process.env.REACT_APP_API_BASE || window.location.origin;

export default function UserPanelModal({ open, onClose }) {
  const { user, isAuthenticated, login } = useAuth();

  const [tab, setTab] = useState('perfil');
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const [form, setForm] = useState({
    nombre: '',
    empresa: '',
    direccion: '',
    whatsapp: '',
    nit: '',
  });
  const [foto, setFoto] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  // Ascenso (PDFs)
  const [dpiPdf, setDpiPdf] = useState(null);
  const [rtuPdf, setRtuPdf] = useState(null);
  const [patentePdf, setPatentePdf] = useState(null);
  const [ascMsg, setAscMsg] = useState('');
  const [ascLoading, setAscLoading] = useState(false);

  const isSubDistrib = useMemo(() => {
    const t = String(user?.tipoUsuario || '').toLowerCase();
    return /sub.?distribuidor/.test(t) || !!user?.isSubdistribuidor || !!user?.subdistribuidor || !!user?.subDistribuidor;
  }, [user]);

  // Campos faltantes del perfil (se memoiza para efectos dependientes)
  const missing = useMemo(() => {
    const faltaEmpresa = !user?.empresa;
    const faltaDireccion = !user?.direccion;
    const faltaWhatsapp = !user?.whatsapp;
    const nit = user?.nit;
    const faltaNit = !nit && nit !== 'CF';
    return faltaEmpresa || faltaDireccion || faltaWhatsapp || faltaNit;
  }, [user?.empresa, user?.direccion, user?.whatsapp, user?.nit]);

  // --- Sólo al ABRIR el modal: reset a 'perfil' e hidratar con snapshot actual
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      wasOpen.current = true;
      setTab('perfil');
      setForm({
        nombre: user?.nombre || user?.nombreCompleto || '',
        empresa: user?.empresa || '',
        direccion: user?.direccion || '',
        whatsapp: user?.whatsapp || '',
        nit: user?.nit || '',
      });
    }
    if (!open && wasOpen.current) {
      wasOpen.current = false;
    }
  }, [open, user?.nombre, user?.nombreCompleto, user?.empresa, user?.direccion, user?.whatsapp, user?.nit]);

  // --- al abrir, si faltan campos, trae /me y NO pisa lo que el usuario tecleó
  useEffect(() => {
    if (!open || !isAuthenticated) return;

    if (!missing) return;

    let ignore = false;
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/auth/web/me`, { credentials: 'include' });
        const j = await r.json().catch(() => ({}));
        if (r.ok && j?.ok && j?.user && !ignore) {
          // Actualiza contexto si llegó info nueva
          login(j.user);

          // Prefiere lo que ya está escrito en form
          setForm((s) => ({
            nombre: s.nombre || j.user.nombre || j.user.nombreCompleto || '',
            empresa: s.empresa || j.user.empresa || '',
            direccion: s.direccion || j.user.direccion || '',
            whatsapp: s.whatsapp || j.user.whatsapp || '',
            nit: s.nit || j.user.nit || '',
          }));
        }
      } catch {
        /* silencioso */
      }
    })();

    return () => { ignore = true; };
  }, [open, isAuthenticated, missing, login]);

  // --- helper pedidos
  const extractPedidos = (j) => {
    if (!j) return [];
    if (Array.isArray(j.data)) return j.data;
    if (Array.isArray(j.pedidos)) return j.pedidos;
    if (j?.data && Array.isArray(j.data.rows)) return j.data.rows;
    return [];
  };

  // --- cargar pedidos cuando se abre la pestaña
  useEffect(() => {
    let ignore = false;
    if (!open || tab !== 'pedidos') return;

    (async () => {
      try {
        setLoading(true);
        setErr('');

        if (!isAuthenticated) {
          const m = await fetch(`${API_BASE}/api/auth/web/me`, { credentials: 'include' });
          if (!m.ok) {
            const jj = await m.json().catch(() => ({}));
            throw new Error(jj?.msg || 'No autenticado');
          }
        }

        const r = await fetch(`${API_BASE}/api/web/panel/pedidos`, { credentials: 'include' });
        const j = await r.json().catch(() => ({}));
        if (!r.ok || !j?.ok) throw new Error(j?.message || j?.msg || 'No autenticado');

        const list = extractPedidos(j);
        if (!ignore) setPedidos(Array.isArray(list) ? list : []);
      } catch (e) {
        if (!ignore) {
          setErr(String(e.message || e));
          setPedidos([]);
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    })();

    return () => { ignore = true; };
  }, [open, tab, isAuthenticated]);

  // --- guardar perfil
  const onSave = async () => {
    try {
      setSaving(true);
      setSaveMsg('');
      const fd = new FormData();
      if (form.nombre) fd.append('nombre', form.nombre);
      if (form.empresa) fd.append('empresa', form.empresa);
      if (form.direccion) fd.append('direccion', form.direccion);
      if (form.whatsapp) fd.append('whatsapp', form.whatsapp);
      if (form.nit) fd.append('nit', form.nit);
      if (foto) fd.append('foto', foto);

      const r = await fetch(`${API_BASE}/api/web/panel/mis-datos`, {
        method: 'PUT',
        body: fd,
        credentials: 'include',
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j?.ok) throw new Error(j?.message || j?.msg || 'No se pudo actualizar');

      // refresca contexto + form si backend devuelve user actualizado
      if (j.user) {
        login(j.user); // <- actualiza AuthContext
        setForm((s) => ({
          nombre: s.nombre || j.user.nombre || j.user.nombreCompleto || '',
          empresa: s.empresa || j.user.empresa || '',
          direccion: s.direccion || j.user.direccion || '',
          whatsapp: s.whatsapp || j.user.whatsapp || '',
          nit: s.nit || j.user.nit || 'CF',
        }));
        setFoto(null);
      }

      setSaveMsg('✔ Datos actualizados');
    } catch (e) {
      setSaveMsg('✖ ' + (e.message || 'Error'));
    } finally {
      setSaving(false);
    }
  };

  // --- solicitud de ascenso
  const solicitarAscenso = async () => {
    try {
      setAscLoading(true);
      setAscMsg('');

      if (!dpiPdf || !rtuPdf || !patentePdf) {
        setAscMsg('✖ Debes adjuntar los tres documentos en PDF: DPI, RTU y Patente.');
        setAscLoading(false);
        return;
      }
      const isPdf = f => f && f.type === 'application/pdf';
      if (!isPdf(dpiPdf) || !isPdf(rtuPdf) || !isPdf(patentePdf)) {
        setAscMsg('✖ Los archivos deben ser PDF.');
        setAscLoading(false);
        return;
      }

      const fd = new FormData();
      fd.append('dpi', dpiPdf);
      fd.append('rtu', rtuPdf);
      fd.append('patente', patentePdf);

      const r = await fetch(`${API_BASE}/api/web/ascenso/solicitud`, {
        method: 'POST',
        body: fd,
        credentials: 'include',
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j?.ok) throw new Error(j?.message || j?.msg || 'No se pudo enviar la solicitud');

      setAscMsg('✔ Solicitud enviada para revisión.');
      setDpiPdf(null); setRtuPdf(null); setPatentePdf(null);
    } catch (e) {
      setAscMsg('✖ ' + (e.message || 'Error al enviar la solicitud'));
    } finally {
      setAscLoading(false);
    }
  };

  const money = (q) => `Q${Number(q || 0).toFixed(2)}`;

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[90]">
      <div
        className="absolute inset-0 bg-slate-900/60"
        role="presentation"
        onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      />
      <div
        className="relative mx-auto w-[92%] max-w-3xl bg-white rounded-2xl soft p-6 sm:p-8 mt-10 max-h-[85vh] overflow-y-auto"
        onKeyDownCapture={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-xl sm:text-2xl font-extrabold" style={{ color: 'var(--ink)' }}>Mi Panel</h3>
          <button className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50" onClick={onClose}>✕</button>
        </div>

        {!isAuthenticated ? (
          <div className="mt-6 p-4 rounded-xl border text-red-600 bg-red-50">
            Debes iniciar sesión para ver tu panel.
          </div>
        ) : (
          <>
            {/* Snapshot */}
            <div className="mt-4 flex items-center gap-3">
              {user?.foto && (
                <img
                  src={user.foto}
                  alt="Foto de perfil"
                  className="h-12 w-12 rounded-full border object-cover"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              )}
              <div className="text-sm text-slate-700">
                <div className="font-semibold">{form.nombre || user?.nombreCompleto || user?.nombre}</div>
                <div className="text-slate-500">{user?.email}</div>
              </div>
            </div>

            {/* Tabs */}
            <div className="mt-5 border-b border-slate-200">
              <div className="flex gap-2">
                <button
                  className={`px-4 py-2 rounded-t-lg border ${tab === 'perfil' ? 'border-slate-200 border-b-white bg-white font-semibold' : 'bg-slate-50 border-transparent'}`}
                  onClick={() => setTab('perfil')}
                >
                  Perfil
                </button>
                <button
                  className={`px-4 py-2 rounded-t-lg border ${tab === 'pedidos' ? 'border-slate-200 border-b-white bg-white font-semibold' : 'bg-slate-50 border-transparent'}`}
                  onClick={() => setTab('pedidos')}
                >
                  Pedidos
                </button>
                {!isSubDistrib && (
                  <button
                    className={`px-4 py-2 rounded-t-lg border ${tab === 'ascenso' ? 'border-slate-200 border-b-white bg-white font-semibold' : 'bg-slate-50 border-transparent'}`}
                    onClick={() => setTab('ascenso')}
                  >
                    Ascenso
                  </button>
                )}
              </div>
            </div>

            {/* PERFIL */}
            {tab === 'perfil' && (
              <section className="mt-6">
                <h4 className="font-semibold text-slate-800 mb-3">Mis datos</h4>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700">Nombre completo</label>
                    <input
                      value={form.nombre}
                      onChange={(e) => setForm((s) => ({ ...s, nombre: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Empresa</label>
                    <input
                      value={form.empresa}
                      onChange={(e) => setForm((s) => ({ ...s, empresa: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-sm font-medium text-slate-700">Dirección</label>
                    <input
                      value={form.direccion}
                      onChange={(e) => setForm((s) => ({ ...s, direccion: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">WhatsApp</label>
                    <input
                      value={form.whatsapp}
                      onChange={(e) => {
                        const v = e.target.value.replace(/\D/g, '').slice(0, 15);
                        setForm((s) => ({ ...s, whatsapp: v }));
                      }}
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">NIT</label>
                    <input
                      value={form.nit}
                      onChange={(e) => setForm((s) => ({ ...s, nit: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                      placeholder="CF o tu NIT"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-sm font-medium text-slate-700">Foto</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setFoto(e.target.files?.[0] || null)}
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 bg-white"
                    />
                  </div>
                </div>

                <div className="mt-3 flex gap-2">
                  <button className="btn btn-brand" onClick={onSave} disabled={saving}>
                    {saving ? 'Guardando…' : 'Guardar cambios'}
                  </button>
                  {!!saveMsg && <div className="self-center text-sm text-slate-600">{saveMsg}</div>}
                </div>
              </section>
            )}

            {/* ASCENSO */}
            {tab === 'ascenso' && !isSubDistrib && (
              <section className="mt-6">
                <h4 className="font-semibold text-slate-800 mb-2">Solicitar ascenso a Distribuidor</h4>
                <p className="text-sm text-slate-600">Adjunta los tres documentos en formato <b>PDF</b>.</p>

                <div className="grid sm:grid-cols-2 gap-4 mt-3">
                  <div className="sm:col-span-2">
                    <label className="text-sm font-medium text-slate-700">DPI (PDF)</label>
                    <input type="file" accept="application/pdf" onChange={(e) => setDpiPdf(e.target.files?.[0] || null)} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 bg-white" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">RTU (PDF)</label>
                    <input type="file" accept="application/pdf" onChange={(e) => setRtuPdf(e.target.files?.[0] || null)} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 bg-white" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Patente de comercio (PDF)</label>
                    <input type="file" accept="application/pdf" onChange={(e) => setPatentePdf(e.target.files?.[0] || null)} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 bg-white" />
                  </div>
                </div>

                <div className="mt-3 flex items-center gap-2">
                  <button className="btn btn-light" onClick={solicitarAscenso} disabled={ascLoading}>
                    {ascLoading ? 'Enviando…' : 'Enviar solicitud'}
                  </button>
                  {!!ascMsg && <div className="text-sm">{ascMsg}</div>}
                </div>
              </section>
            )}

            {/* PEDIDOS */}
            {tab === 'pedidos' && (
              <section className="mt-6">
                <h4 className="font-semibold text-slate-800 mb-3">Mis pedidos</h4>
                {loading ? (
                  <div className="text-slate-500">Cargando…</div>
                ) : err ? (
                  <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{err}</div>
                ) : pedidos.length === 0 ? (
                  <div className="text-slate-500">No tienes pedidos aún.</div>
                ) : (
                  <div className="space-y-3">
                    {pedidos.map((p) => (
                      <div key={p.id} className="p-3 rounded-xl border flex items-center justify-between">
                        <div>
                          <div className="font-semibold">Pedido #{p.id}</div>
                          <div className="text-sm text-slate-600">Estado: {p.estado || '—'}</div>
                        </div>
                        <div className="text-right">
                          <div className="font-semibold">{money(p.total)}</div>
                          <div className="text-xs text-slate-500">
                            {p.fecha
                              ? new Date(p.fecha).toLocaleString()
                              : (p.creadoEn ? new Date(p.creadoEn).toLocaleString() : '')}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}
